import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Button from '../components/Button';
import { logger } from '../utils/logger';

const AcceptInvite: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteAccepted, setInviteAccepted] = useState(false);
  const [groupName, setGroupName] = useState<string | null>(null);

  useEffect(() => {
    // If user is not logged in, redirect to login with return URL
    if (!user) {
      // Store the invite token in localStorage so we can process it after login
      localStorage.setItem('pendingInviteToken', token || '');
      navigate(`/login?redirect=/invite/${token}`);
      return;
    }

    // Process the invitation
    const acceptInvitation = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await api.post(`/family-groups/invite/${token}`);
        setInviteAccepted(true);
        setGroupName(response.data.familyGroup.name);
        
        // Remove any stored pending invite token
        localStorage.removeItem('pendingInviteToken');
      } catch (err: any) {
        logger.error('Error accepting invitation:', err);
        if (err.response?.data?.message) {
          setError(err.response.data.message);
        } else {
          setError('Failed to accept invitation. It may be invalid or expired.');
        }
      } finally {
        setLoading(false);
      }
    };

    acceptInvitation();
  }, [token, user, navigate]);

  const handleGoToGroups = () => {
    navigate('/groups');
  };

  const handleGoToHome = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8 p-10 bg-white rounded-xl shadow-md">
          <div className="text-center">
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Processing Invitation</h2>
            <p className="mt-2 text-sm text-gray-600">Please wait while we process your invitation...</p>
          </div>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8 p-10 bg-white rounded-xl shadow-md">
          <div className="text-center">
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Invitation Error</h2>
            <div className="mt-4 text-red-600">{error}</div>
            <div className="mt-6">
              <Button onClick={handleGoToHome} variant="primary">
                Go to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-10 bg-white rounded-xl shadow-md">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {inviteAccepted ? "Invitation Accepted!" : "Processing Invitation"}
          </h2>
          {groupName && (
            <p className="mt-2 text-lg text-gray-600">
              You have successfully joined <span className="font-semibold">{groupName}</span>.
            </p>
          )}
          <div className="mt-6">
            <Button onClick={handleGoToGroups} variant="primary">
              Go to Groups
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite; 